import {
  type AgenticLogicValidationScenarioInput,
  AgenticLogicValidationScenarioInputSchema,
} from "@intelligems/ig-types/functions/agentic-logic/gates/validation-scenario";
import {
  EvaluateAgenticLogicRuleScenarioResponseBodySchema,
  type EvaluateAgenticLogicRuleScenarioResponseBodyType,
} from "@intelligems/ig-types/functions/agentic-logic/rule-scenarios/rule-scenarios";
import { type UiStreamAction } from "@intelligems/ig-types/schemas/ai/ui-stream-actions";

import { readToolJsonResults } from "@src/features/ai/utils/tool-json-results";

export const EVALUATE_SCENARIO_TOOL_NAME =
  "evaluate_agentic_logic_rule_scenario";

/** The request a cart run evaluated: surface, cart, shipping, customer. */
export type AgenticLogicScenarioRunRequest =
  AgenticLogicValidationScenarioInput["request"];

/**
 * One completed cart run, as the workspace needs it: which rule, which cart
 * (identity on the wire is the scenario NAME), the request that ran, and the
 * evaluation itself.
 */
export interface AgenticLogicScenarioRun {
  ruleId: string;
  scenarioName: string;
  /**
   * The tool input's request — the single source for what actually ran,
   * whether the cart was one of the rule's named examples or authored in
   * the chat. Null when the input did not parse; the canvas then shows the
   * evaluation with no scenario caption rather than blocking the run.
   */
  request: AgenticLogicScenarioRunRequest | null;
  response: EvaluateAgenticLogicRuleScenarioResponseBodyType;
}

export interface AgenticLogicScenarioCall {
  ruleId: string;
  scenarioName: string;
  request: AgenticLogicScenarioRunRequest | null;
}

export interface AgenticLogicScenarioTracker {
  /** The rule/cart a call named, kept until its output arrives. */
  pendingByToolCallId: Record<string, AgenticLogicScenarioCall>;
  /** The most recent completed run; a new object identity marks a new run. */
  lastRun: AgenticLogicScenarioRun | null;
}

export const createAgenticLogicScenarioTracker =
  (): AgenticLogicScenarioTracker => ({
    pendingByToolCallId: {},
    lastRun: null,
  });

/**
 * Watches the chat stream for cart runs.
 *
 * Atlas is the only thing that runs a cart — the graph canvas has no picker of
 * its own — so this reducer is the whole channel between the tool result and
 * the canvas beside it. The call's INPUT names the rule and the cart; its
 * OUTPUT carries the evaluation, which is why both halves are correlated by
 * `toolCallId` rather than read from either one alone.
 *
 * Deliberately separate from `trackAgenticLogicCreation`: that tracker stops
 * at the writes that finish a rule, and every cart run happens after them.
 */
export function trackAgenticLogicScenarioRun(
  tracker: AgenticLogicScenarioTracker,
  action: UiStreamAction
): AgenticLogicScenarioTracker {
  if (action.type !== "addStreamPart") {
    return tracker;
  }
  const part = action.streamPart;

  if (part.type === "tool-input-available") {
    if (part.toolName !== EVALUATE_SCENARIO_TOOL_NAME) {
      return tracker;
    }
    const call = readAgenticLogicScenarioCall(part.input);
    if (!call) {
      return tracker;
    }
    return {
      ...tracker,
      pendingByToolCallId: {
        ...tracker.pendingByToolCallId,
        [part.toolCallId]: call,
      },
    };
  }

  if (part.type !== "tool-output-available") {
    return tracker;
  }
  const call = tracker.pendingByToolCallId[part.toolCallId];
  if (!call) {
    return tracker;
  }

  // Consumed either way: a replayed transcript re-emits the same output, and
  // a run must not fire twice off one call.
  const consumed = { ...tracker.pendingByToolCallId };
  delete consumed[part.toolCallId];

  const results = readToolJsonResults(part.output);
  if (!results) {
    return { ...tracker, pendingByToolCallId: consumed };
  }

  // Parsed, not cast: the tool output carries `executionTime`/`message`
  // alongside the response body, and what gets seeded into the evaluation
  // cache has to be exactly what the route itself would have answered.
  const parsed =
    EvaluateAgenticLogicRuleScenarioResponseBodySchema.safeParse(results);
  if (!parsed.success) {
    return { ...tracker, pendingByToolCallId: consumed };
  }

  return {
    pendingByToolCallId: consumed,
    lastRun: { ...call, response: parsed.data },
  };
}

/** The rule and cart an evaluate-tool input names, or null when it names none. */
export function readAgenticLogicScenarioCall(
  input: unknown
): AgenticLogicScenarioCall | null {
  if (!input || typeof input !== "object") {
    return null;
  }
  const fields = input as Record<string, unknown>;
  const ruleId = readNonEmptyString(fields.ruleId);
  const scenario = fields.scenario;
  if (!ruleId || !scenario || typeof scenario !== "object") {
    return null;
  }
  const scenarioName = readNonEmptyString(
    (scenario as Record<string, unknown>).name
  );
  if (!scenarioName) {
    return null;
  }
  // Parsed against the tool's own input contract so the canvas reads typed
  // shipping/customer blocks; a request that does not parse is not a reason
  // to drop the run — the evaluation still happened.
  const request =
    AgenticLogicValidationScenarioInputSchema.shape.request.safeParse(
      (scenario as Record<string, unknown>).request
    );
  return {
    ruleId,
    scenarioName,
    request: request.success ? request.data : null,
  };
}

function readNonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}
